import CancelOutlined from "@mui/icons-material/CancelOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  ListItemButtonProps,
  ListItemProps,
  Radio,
  RadioGroup,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useAddNotebookPageAsync, newNotebookPage, useRootFolderId } from "../../hook/page";
import { useAppId } from "../../hook";
import { useSideNavMenu } from "../../hook/sideNav";
import { ModalWindow } from "../modal";
import {
  IconButtonSmall,
  ListItemButtonSmall,
  ListItemSmall,
  ListItemTextSmall,
} from "./listItem";

export const SideNavMenu: React.FC<{
  children?: React.ReactNode;
  name: string;
  props?: ListItemProps;
  href?: string;
  buttons?: React.ReactNode;
  buttonProps?: ListItemButtonProps;
  testId?: string;
}> = React.memo(({ children, name, href, buttons, buttonProps, props, testId }) => {
  const { toggleIsOpen, isOpen } = useSideNavMenu();
  const propsSxParam: SxProps<Theme> = props?.sx ?? {};
  const defaultPropsSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "start",
  };
  const finalSxProps: SxProps<Theme> = { ...defaultPropsSx, ...propsSxParam };
  const finalProps: ListItemProps = { ...props, sx: finalSxProps };

  return (
    <>
      <ListItemSmall {...finalProps}>
        <IconButtonSmall
          onClick={toggleIsOpen}
          color="primary"
          edge="start"
          aria-label="open"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {isOpen ? <ExpandLessIcon /> : <ChevronRight />}
        </IconButtonSmall>
        {href ? (
          <Link href={href} data-testid={testId ? `${testId}-link` : undefined}>
            <ListItemButtonSmall {...buttonProps}>
              <ListItemTextSmall primary={name} />
            </ListItemButtonSmall>
          </Link>
        ) : (
          <ListItemTextSmall primary={name} />
        )}
        {buttons}
      </ListItemSmall>
      <Collapse in={isOpen} data-testid={testId ? `${testId}-children` : undefined}>
        {children}
      </Collapse>
    </>
  );
});

export const AddPageModal: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ open, setOpen }) => {
  const addNotebookPageAsync = useAddNotebookPageAsync();
  const rootFolderId = useRootFolderId();
  const [appId] = useAppId();
  const [formData, setFormData] = useState({
    pageType: "note",
    pageName: "",
  });
  const [emptyNameError, setEmptyNameError] = useState(false);
  const pageType = formData.pageType;
  const pageName = formData.pageName;
  const setPageName = (pageName: string) =>
    setFormData((formData) => ({ ...formData, pageName }));
  const setPageType = (pageType: string) =>
    setFormData((formData) => ({ ...formData, pageType }));

  const onSubmit = () => {
    if (pageName === "") {
      setEmptyNameError(true);
      return;
    }
    if (!rootFolderId || !appId) return;
    setEmptyNameError(false);
    const isDatabase = pageType === "database";
    const page = newNotebookPage({ name: pageName, folderId: rootFolderId, appId, isDatabase });
    addNotebookPageAsync(page);
    setOpen(false);
  };

  return (
    <ModalWindow open={open}>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Add Page</Typography>
          <IconButton onClick={() => setOpen(false)} color="primary">
            <CancelOutlined />
          </IconButton>
        </Box>
        <Box
          sx={{
            gap: 2,
            display: "flex",
            alignItems: "start",
            flexDirection: "column",
          }}
        >
          <FormControl>
            <FormLabel id="page-type-radio-buttons">Page Type</FormLabel>
            <RadioGroup
              value={pageType}
              row
              onChange={(e) => setPageType(e.target.value)}
              aria-labelledby="page-type-radio-buttons"
              defaultValue="note"
              name="page-type"
            >
              <FormControlLabel
                value={"note"}
                control={<Radio />}
                label="Note Page"
              />
              <FormControlLabel
                value={"database"}
                control={<Radio />}
                label="Database Page"
              />
            </RadioGroup>
          </FormControl>
          <TextField
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            label="Name"
            error={emptyNameError}
            helperText={emptyNameError ? "Cannot Be Empty" : undefined}
          />
          <Button onClick={onSubmit}>Save</Button>
        </Box>
      </Box>
    </ModalWindow>
  );
};
